import Microgame from '../engine/Microgame.js';

export default class DefendGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Défends !`, en: `Defend!`, es: `¡Defiende!` },
            controls: { fr: `Appuie sur les missiles qui s'approchent (ou glisse dessus)`, en: `Tap the approaching missiles (or swipe them)`, es: `Toca los misiles que se acercan (o desliza sobre ellos)` },
            objective: { fr: `Protège ta base jusqu'à la fin`, en: `Protect your base until time is up`, es: `Protege tu base hasta que termine el tiempo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--secondary-color)" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
            actions: [
                { type: 'wait', duration: 560 },
                { type: 'tapTarget', selector: '[data-playback-missile="true"]', duration: 220 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 2400
        };
    }
    constructor(engine) {
        super(engine);
        this.missiles = [];
        this.core = null;
        this.spawnTimer = 0;
        this.score = 0;

        // In STANDARD mode, 1 tap = win.
        if (this.engine.activeChallenge && this.engine.activeChallenge.config && this.engine.activeChallenge.config.targetScore) {
            this.targetScore = this.engine.activeChallenge.config.targetScore;
        } else if (this.engine.activeChallenge) {
            this.targetScore = Infinity; // Survive in challenge by default
        } else {
            this.targetScore = 1; // ALL GAMES mode
        }

        // Spawn rate depends on game mode
        const sm = this.engine.speedMultiplier || 1.0;
        if (this.engine.gameMode === 'STANDARD') {
            // In ALL GAMES: spawn 2 missiles during the round for visual variety
            // Game time = 3000/sm ms. Spawn a 2nd missile at ~40% of game time.
            this.spawnRate = (3000 / sm / 1000) * 0.4; // e.g. x1 → 1.2s, x4 → 0.3s
        } else {
            this.spawnRate = 1.5;
        }
    }

    setup() {
        this.engine.ui.instruction.textContent = "DEFEND!";

        // Core (center sphere to protect)
        this.core = document.createElement('div');
        this.core.style.position = 'absolute';
        this.core.style.top = '50%';
        this.core.style.left = '50%';
        this.core.style.transform = 'translate(-50%, -50%)';
        this.core.style.width = '60px';
        this.core.style.height = '60px';
        this.core.style.background = this.primaryColor;
        this.core.style.borderRadius = '50%';
        this.core.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        this.core.style.zIndex = '10';
        this.container.appendChild(this.core);

        this.spawnMissile();
    }

    spawnMissile() {
        const containerW = this.container.clientWidth || window.innerWidth;
        const containerH = this.container.clientHeight || window.innerHeight;
        const missileSize = 70;
        const missileRadius = missileSize / 2; // 35
        const coreRadius = 30;

        // Spawn from any of the 4 edges for variety
        const edge = this.isPlayback ? 0 : Math.floor(Math.random() * 4); // 0=top, 1=bottom, 2=left, 3=right
        let startX, startY;
        switch (edge) {
            case 0: // Top
                startX = Math.random() * containerW;
                startY = -missileSize;
                break;
            case 1: // Bottom
                startX = Math.random() * containerW;
                startY = containerH + missileSize;
                break;
            case 2: // Left
                startX = -missileSize;
                startY = Math.random() * containerH;
                break;
            case 3: // Right
                startX = containerW + missileSize;
                startY = Math.random() * containerH;
                break;
        }

        // Target = container center (where the core is)
        const cx = containerW / 2;
        const cy = containerH / 2;

        // Direction from missile CENTER to core CENTER
        const missileCenterX = startX + missileRadius;
        const missileCenterY = startY + missileRadius;
        const dx = cx - missileCenterX;
        const dy = cy - missileCenterY;
        const len = Math.sqrt(dx * dx + dy * dy);

        if (len === 0) return; // Safety

        // Collision distance: core radius + missile radius
        const collisionDist = coreRadius + missileRadius; // 65

        // Calculate speed based on FIXED reaction time (in seconds)
        // The reaction time = how long the missile is on screen before hitting the core
        // Scales linearly: 1.5s at x1.0 → 0.5s at x4.0
        const sm = Math.min(this.engine.speedMultiplier || 1.0, 4.0);
        const reactionTime = Math.max(0.4, 1.5 - ((sm - 1.0) / 3.0) * 1.0);
        const distToCollision = Math.max(10, len - collisionDist);
        let speed = distToCollision / reactionTime;

        // Create missile element
        const missile = document.createElement('div');
        missile.style.position = 'absolute';
        missile.style.left = `${startX}px`;
        missile.style.top = `${startY}px`;
        missile.style.width = `${missileSize}px`;
        missile.style.height = `${missileSize}px`;
        missile.style.background = this.secondaryColor;
        missile.style.borderRadius = '50%';
        missile.style.cursor = 'pointer';
        missile.style.boxShadow = `0 0 15px ${this.secondaryColor}`;
        missile.style.zIndex = '20';
        missile.style.userSelect = 'none';
        missile.style.touchAction = 'none';
        if (this.isPlayback && this.missiles.length === 0) {
            missile.dataset.playbackMissile = 'true';
        }

        missile.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.destroyMissile(missile);
        });

        this.container.appendChild(missile);
        this.missiles.push({
            el: missile,
            x: startX,
            y: startY,
            vx: (dx / len) * speed,
            vy: (dy / len) * speed
        });
    }

    destroyMissile(el) {
        const index = this.missiles.findIndex(m => m.el === el);
        if (index > -1) {
            this.missiles.splice(index, 1);
            el.remove();

            this.score++;
            if (this.score >= this.targetScore) {
                this.win();
            }
        }
    }

    // Engine calls this when timer reaches 0 — survive = win!
    onTimeout() {
        return true;
    }

    update(dt) {
        // Spawn additional missiles over time
        this.spawnTimer += dt / 1000;
        if (this.spawnTimer > this.spawnRate) {
            this.spawnMissile();
            this.spawnTimer = 0;
            this.spawnRate = Math.max(0.3, this.spawnRate * 0.95);
        }

        // Time-based Scoring for challenge/infinite modes
        if (this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack))) {
            this.scoreAccumulator = (this.scoreAccumulator || 0) + dt;
            if (this.scoreAccumulator >= 2000) {
                this.scoreAccumulator -= 2000;
                this.engine.score++;
                this.engine.updateHUD();
            }
            this.globalSpeedMult = (this.globalSpeedMult || 1) + (dt / 10000);
        }

        // Collision detection
        const containerW = this.container.clientWidth || window.innerWidth;
        const containerH = this.container.clientHeight || window.innerHeight;
        const cx = containerW / 2;
        const cy = containerH / 2;
        const missileRadius = 35; // 70px / 2
        const coreRadius = 30;    // 60px / 2
        const collisionDist = coreRadius + missileRadius; // 65

        for (let i = this.missiles.length - 1; i >= 0; i--) {
            const m = this.missiles[i];

            // Update position
            m.x += m.vx * (dt / 1000) * (this.globalSpeedMult || 1);
            m.y += m.vy * (dt / 1000) * (this.globalSpeedMult || 1);

            m.el.style.left = `${m.x}px`;
            m.el.style.top = `${m.y}px`;

            // Distance from missile CENTER to core CENTER
            const missileCX = m.x + missileRadius;
            const missileCY = m.y + missileRadius;
            const dist = Math.sqrt((cx - missileCX) ** 2 + (cy - missileCY) ** 2);

            if (dist < collisionDist) {
                this.lose();
                return;
            }
        }
    }
}
